// Buyer-facing fee model.
//
// There is NO generic product tax charged to the buyer — VAT is only ever
// charged to the SELLER, on their commission (see AdminDashboard's Service
// Charges section / backend models.CommissionBreakdown), combined into one
// "X% (VAT included)" figure and deducted from their payout.
//
// The buyer instead sees two distinct, purpose-specific charges:
//   - Logistics Charge: the real shipping/logistics fee, plus VAT on that
//     fee only (same admin-configurable VAT % used for seller commission),
//     shown combined as one "₦X (VAT included)" figure.
//   - Escrow Fee: a percentage of the product subtotal, charged only when
//     the buyer opts into escrow protection at checkout — covers the cost
//     of Pandascrow holding funds. Admin-configurable (Admin → Settings →
//     Escrow Fee).
//
// Both rates come from one shared, cached fetch so Cart/Checkout never
// duplicate the numbers or drift out of sync with each other.

import { settingsAPI } from '../services/api';

// Matches the backend's models.DefaultVATPercentage / DefaultEscrowFeePercentage.
export const DEFAULT_VAT_PERCENTAGE = 7.5;
export const DEFAULT_ESCROW_FEE_PERCENTAGE = 3;

// Applied promo code, backend-validated in Cart and carried through to
// Checkout via sessionStorage (Cart and Checkout are separate route views —
// there's no other shared client state between them for this).
export const APPLIED_PROMO_STORAGE_KEY = 'autoboy_applied_promo';

const DEFAULT_RATES = { vatPercentage: DEFAULT_VAT_PERCENTAGE, escrowFeePercentage: DEFAULT_ESCROW_FEE_PERCENTAGE };

let cachedRates = null;
let inFlightRequest = null;

// Fetches {vatPercentage, escrowFeePercentage} once, caching the result for
// the lifetime of the page. Falls back to the defaults above if the request
// fails.
export const getCheckoutFeeRates = async () => {
  if (cachedRates != null) return cachedRates;
  if (!inFlightRequest) {
    inFlightRequest = settingsAPI.getCheckoutFeeRates()
      .then((res) => {
        const data = res?.data ?? res;
        const vatPercentage = typeof data?.vat_percentage === 'number' && data.vat_percentage > 0
          ? data.vat_percentage
          : DEFAULT_VAT_PERCENTAGE;
        const escrowFeePercentage = typeof data?.escrow_fee_percentage === 'number' && data.escrow_fee_percentage > 0
          ? data.escrow_fee_percentage
          : DEFAULT_ESCROW_FEE_PERCENTAGE;
        cachedRates = { vatPercentage, escrowFeePercentage };
        return cachedRates;
      })
      .catch(() => {
        cachedRates = DEFAULT_RATES;
        return cachedRates;
      })
      .finally(() => {
        inFlightRequest = null;
      });
  }
  return inFlightRequest;
};

// Logistics VAT: VAT on the shipping fee only, rounded to the nearest naira.
export const calculateLogisticsVat = (shippingFee, vatPercentage) =>
  Math.round(shippingFee * (vatPercentage / 100));

// Escrow Fee: a percentage of the product subtotal (never shipping/VAT),
// rounded to the nearest naira. Callers should only add this to the total
// when the buyer has escrow protection enabled.
export const calculateEscrowFee = (subtotal, escrowFeePercentage) =>
  Math.round(subtotal * (escrowFeePercentage / 100));
